import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  Text, 
  Badge, 
  makeStyles, 
  tokens
} from '@fluentui/react-components';
import { 
  ArrowLeftRegular, 
  HomeRegular, 
  CopyRegular,
  CheckmarkRegular 
} from '@fluentui/react-icons';
import { Issue, Priority } from '../types';

interface DetailViewProps {
  issue: Issue;
  onBack: () => void;
}

const useStyles = makeStyles({
  container: {
    maxWidth: '64rem',
    margin: '0 auto',
  },
  buttonGroup: {
    display: 'flex',
    gap: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: tokens.spacingVerticalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: tokens.spacingVerticalL,
  },
  pathCode: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase500,
  },
  taxonomyContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalL,
  },
  section: {
    marginBottom: tokens.spacingVerticalL,
  },
  sectionTitle: {
    marginBottom: tokens.spacingVerticalM,
    display: 'block',
  },
  metadataGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: tokens.spacingVerticalL,
    marginBottom: tokens.spacingVerticalL,
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingVerticalS,
  },
  orderedList: {
    paddingLeft: '1.5rem',
    marginTop: tokens.spacingVerticalS,
  },
  listItem: {
    marginBottom: tokens.spacingVerticalS,
  },
  priorityBadge: {
    display: 'inline-block',
  },
});

const getPriorityBadgeAppearance = (priority: Priority): 'filled' => {
  return 'filled';
};

const getPriorityColor = (priority: Priority): string => {
  const colors: Record<Priority, string> = {
    Critical: '#d13438',
    High: '#ca5010',
    Medium: '#f2c811',
    Low: '#107c10',
  };
  return colors[priority] || tokens.colorNeutralForeground3;
};

const DetailView: React.FC<DetailViewProps> = ({ issue, onBack }) => {
  const [copied, setCopied] = useState<boolean>(false);
  const styles = useStyles();

  const copyToClipboard = (): void => {
    navigator.clipboard.writeText(issue.Path).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const formatResolutionSteps = (procedure: string): string[] => {
    // Split by numbered steps (1., 2., etc.)
    const steps = procedure.split(/(?=\d+\.\s)/).filter(step => step.trim());
    return steps.map((step) => step.trim());
  };

  if (!issue) {
    return (
      <div style={{ textAlign: 'center', padding: tokens.spacingVerticalXXL }}>
        <Text>No issue selected</Text>
      </div>
    );
  }

  const steps = formatResolutionSteps(issue.ResolutionProcedure);

  return (
    <div className={styles.container}>
      <div className={styles.buttonGroup}>
        <Button
          icon={<ArrowLeftRegular />}
          onClick={onBack}
          appearance="subtle"
        >
          Back
        </Button>
        <Button
          icon={<HomeRegular />}
          onClick={() => (window.location.href = '/')}
          appearance="subtle"
        >
          Home
        </Button>
      </div>

      <Card>
        <div style={{ padding: '2rem' }}>
          {/* Path with Copy Button */}
          <div className={styles.header}>
            <div>
              <Text as="h2" size={600} weight="bold" style={{ marginBottom: tokens.spacingVerticalS }}>
                Path <span className={styles.pathCode}>{issue.Path}</span>
              </Text>
            </div>
            <Button
              icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
              onClick={copyToClipboard}
              appearance={copied ? 'primary' : 'primary'}
              style={{
                backgroundColor: copied ? tokens.colorPaletteGreenBackground3 : undefined,
              }}
            >
              {copied ? 'Copied!' : 'Copy Path'}
            </Button>
          </div>

          {/* Taxonomy Path */}
          <div className={styles.section}>
            <Text as="h3" size={300} weight="semibold" className={styles.sectionTitle}>
              Taxonomy
            </Text>
            <div className={styles.taxonomyContainer}>
              <Badge appearance="filled" color="brand">
                {issue.Taxonomy.Domain}
              </Badge>
              <Badge appearance="filled" color="brand">
                {issue.Taxonomy.Category}
              </Badge>
              <Badge appearance="filled" color="brand">
                {issue.Taxonomy.Subcategory}
              </Badge>
              <Badge appearance="filled" color="brand">
                {issue.Taxonomy.Issue}
              </Badge>
              <Badge appearance="filled" color="brand">
                {issue.Taxonomy.RootCause}
              </Badge>
            </div>
          </div>

          {/* Description */}
          <div className={styles.section}>
            <Text as="h3" size={500} weight="semibold" className={styles.sectionTitle}>
              Description
            </Text>
            <Text style={{ display: 'block' }}>{issue.Description}</Text>
          </div>

          {/* Resolution Procedure */}
          <div className={styles.section}>
            <Text as="h3" size={500} weight="semibold" className={styles.sectionTitle}>
              Resolution Procedure
            </Text>
            <ol className={styles.orderedList}>
              {steps.map((step, index) => (
                <li key={index} className={styles.listItem}>
                  <Text>{step.replace(/^\d+\.\s*/, '')}</Text>
                </li>
              ))}
            </ol>
          </div>

          {/* Metadata Grid */}
          <div className={styles.metadataGrid}>
            <div>
              <Text as="h4" size={300} weight="semibold" style={{ 
                color: tokens.colorNeutralForeground3,
                marginBottom: tokens.spacingVerticalXS,
                display: 'block'
              }}>
                Responsible Team
              </Text>
              <Text>{issue.ResponsibleTeam}</Text>
            </div>
            <div>
              <Text as="h4" size={300} weight="semibold" style={{ 
                color: tokens.colorNeutralForeground3,
                marginBottom: tokens.spacingVerticalXS,
                display: 'block'
              }}>
                Priority
              </Text>
              <Badge
                appearance={getPriorityBadgeAppearance(issue.Priority)}
                style={{
                  backgroundColor: getPriorityColor(issue.Priority),
                  color: 'white',
                }}
              >
                {issue.Priority}
              </Badge>
            </div>
            <div>
              <Text as="h4" size={300} weight="semibold" style={{ 
                color: tokens.colorNeutralForeground3,
                marginBottom: tokens.spacingVerticalXS,
                display: 'block'
              }}>
                Estimated Resolution Time
              </Text>
              <Text>{issue.EstimatedResolutionTime} minutes</Text>
            </div>
            <div>
              <Text as="h4" size={300} weight="semibold" style={{ 
                color: tokens.colorNeutralForeground3,
                marginBottom: tokens.spacingVerticalXS,
                display: 'block'
              }}>
                Common Issue
              </Text>
              <Text>{issue.Common ? 'Yes' : 'No'}</Text>
            </div>
          </div>

          {/* Tags */}
          {issue.Tags && issue.Tags.length > 0 && (
            <div>
              <Text as="h4" size={300} weight="semibold" style={{ 
                color: tokens.colorNeutralForeground3,
                marginBottom: tokens.spacingVerticalS 
              }}>
                Tags
              </Text>
              <div className={styles.tagsContainer}>
                {issue.Tags.map((tag, index) => (
                  <Badge key={index} appearance="outline">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DetailView;

